import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Box, Stack, useTheme } from '@mui/material'
import { gql, useQuery } from '@apollo/client'
import moment from 'moment'
import { endOfDay, startOfDay } from 'date-fns'
import { HotspotHistory, TimeRange } from 'models'
import ChartSkeleton from 'components/global/CUK/Skeletons/ChartSkeleton'
import { Area, AreaChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'

import { CTColor, CTData, CTLine, CTTitle, CustomTooltipContainer } from './ActiveUsersStyles'

import {
  getTicksOfEachNewDays,
  getTicksOfEachNewHours,
  getTicksOfEachNewMonths,
  getTicksOfEachNewYears
} from './ActiveUsersUtils'

import Title from 'components/global/Title'
import ZeroStateMissingData from 'components/global/ZeroStateMissingData'
import prettyBytes from 'pretty-bytes'
import DateFilter from 'components/DateFilter/DateFilter'
import { useSites } from 'store'

const OPERATION_TRAFFIC_DOWNLOAD_UPLOAD = gql`
    query monitoringGraphWan($sf_ids: [String], $start : String, $end: String) {
        monitoringGraphWan(sf_ids: $sf_ids, start: $start, end: $end) {
            timestamps
            metrics {
                key
                values
            }
        }
    }
`

const CustomTooltip = ({ active, payload }) => {
  const { t, i18n } = useTranslation()

  if (active && payload && payload.length) {
    return (
      <CustomTooltipContainer>
        <CTTitle>
          {new Intl.DateTimeFormat(i18n.language, {
            dateStyle: 'full',
            timeStyle: 'short',
          }).format(payload[0].payload.timestamp)}
        </CTTitle>
        {payload.reverse().map((item) => {
          const prettyBytesValue = prettyBytes(item.value * 1_000, {
            locale: i18n.language,
          })
          return (
            <CTLine key={Math.random()}>
              <CTColor color={item.fill} />
              <CTData>
                <Box>{t(`stats.legends.${item.name}`)}</Box>
                <Box>{prettyBytesValue.replace('B', 'b') + '/s'}</Box>
              </CTData>
            </CTLine>
          )
        })}
        <Stack
          sx={{
            flexDirection: 'row',
            justifyContent: 'space-between',
            paddingLeft: '16px',
          }}
        ></Stack>
      </CustomTooltipContainer>
    )
  }
  return null
}

const CustomizedLegend = ({ payload }) => {
  const theme = useTheme()
  const { t } = useTranslation()

  return (
    <Stack
      sx={{
        justifyContent: 'space-between',
        flexDirection: 'row',
        fontSize: theme.spacing(1.5),
        gap: theme.spacing(2),
        padding: theme.spacing(0, 0, 2),
      }}
    >
      {payload.map((item) => {
        let icon: JSX.Element | null = null
        if (item.value === 'upload') {
          icon = <i className="fa-solid fa-arrow-up" style={{ fontSize: '8px', color: 'white' }} />
        } else if (item.value === 'download') {
          icon = <i className="fa-solid fa-arrow-down" style={{ fontSize: '8px', color: 'white' }} />
        }

        return (
          <Stack
            key={item.value}
            sx={{
              flexDirection: 'row',
              alignItems: 'center',
              lineHeight: '16px',
              gap: theme.spacing(0.5),
            }}
          >
            <Box
              sx={{
                width: '12px',
                height: '12px',
                borderRadius: '50%',
                backgroundColor: item.color,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {icon}
            </Box>
            {t(`stats.legends.${item.value}`)}
          </Stack>
        )
      })}
    </Stack>
  )
}

interface ChartProps {
  data: HotspotHistory
  range: TimeRange
}

const Chart = ({ data, range }: ChartProps) => {
  const theme = useTheme()
  const { i18n } = useTranslation()

  const customTickFormatter = (tick: string) => {
    const date = new Date(tick)
    if (!range) {
      return date.toLocaleDateString([], { year: 'numeric' })
    } else if (range === TimeRange.Today) {
      return date.toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit',
      })
    } else if ([TimeRange.LastSevenDays].includes(range)) {
      return date.toLocaleDateString([], { day: 'numeric', month: 'numeric' })
    } else if ([TimeRange.LastThirtyDays].includes(range)) {
      return date.toLocaleDateString([], { day: 'numeric', month: 'numeric' })
    } else if ([TimeRange.LastTwelveMonths].includes(range)) {
      return date.toLocaleDateString([], { month: 'short' })
    } else {
      return date.toLocaleDateString([], { year: 'numeric' })
    }
  }

  const yAxisFormatter = (value: number) => {
    const prettyBytesValue = prettyBytes(value * 1_000, {
      locale: i18n.language,
    })
    return prettyBytesValue.replace('B', 'b') + '/s'
  }

  const formattedData = useMemo(() => {
    return data.timestamps.map((timestamp, index) => ({
      timestamp: +data.timestamps[index],
      dateTime: new Date(+data.timestamps[index]).toLocaleString([], {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
      }),
      download: Math.floor(
        data.metrics.find((metric) => metric.key === 'download')?.values[
        index
        ] || 0,
      ),
      upload: Math.floor(
        data.metrics.find((metric) => metric.key === 'upload')?.values[index] ||
        0,
      ),
    }))
  }, [data])

  const ticks = useMemo(() => {
    if (!range) {
      getTicksOfEachNewYears(formattedData)
    } else if (range === TimeRange.Today) {
      return getTicksOfEachNewHours(formattedData)
    } else if ([TimeRange.LastSevenDays].includes(range)) {
      return getTicksOfEachNewDays(formattedData)
    } else if ([TimeRange.LastThirtyDays].includes(range)) {
      return getTicksOfEachNewDays(formattedData)
    } else if ([TimeRange.LastTwelveMonths].includes(range)) {
      return getTicksOfEachNewMonths(formattedData)
    } else {
      return getTicksOfEachNewYears(formattedData)
    }
  }, [range, formattedData])

  return (
    <ResponsiveContainer>
      <AreaChart
        data={formattedData.slice(0, -1)}
        height={400}
        margin={{
          top: -20,
          right: 0,
          left: -5,
          bottom: 0,
        }}
      >
        <CartesianGrid
          strokeDasharray="3 3"
          stroke={theme.palette.grey['200']}
        />
        <XAxis
          dataKey={'timestamp'}
          type={'category'}
          domain={['auto', 'auto']}
          tickFormatter={customTickFormatter}
          allowDataOverflow={true}
          ticks={ticks}
          height={48}
          angle={-35}
          tick={{ textAnchor: 'end', transform: 'translate(0,15)', fontSize: 10 }}
          includeHidden
        />
        <YAxis
          tickFormatter={yAxisFormatter}
          tick={{ fontSize: 10 }}
          axisLine={false}
          tickLine={false}
        />
        <Tooltip
          wrapperStyle={{ outline: 'none' }}
          content={<CustomTooltip active={undefined} payload={undefined} />}
        />
        <Legend
          content={<CustomizedLegend payload={undefined} />}
          layout="horizontal"
          verticalAlign="top"
          wrapperStyle={{
            display: 'flex',
            justifyContent: 'flex-start',
            alignItems: 'center',
            height: '30px',
            marginLeft: '5px',
          }}
        />
        <Area
          type="monotone"
          dataKey="upload"
          stackId="1"
          stroke="#FDAE61"
          fill="#FDAE61"
        />
        <Area
          type="monotone"
          dataKey="download"
          stackId="1"
          stroke="#59B5D9"
          fill="#59B5D9"
        />
      </AreaChart>
    </ResponsiveContainer>
  )
}
const ActiveUsers = () => {
  const { t } = useTranslation()
  const [{ selected, focus }] = useSites()
  const [range, setRange] = useState<null | TimeRange>(TimeRange.LastSevenDays)
  const [{ start, end }, setPeriod] = useState<{ start: null | Date, end: null | Date }>({ start: null, end: null })
  const periodStart = useMemo(() => start ? Math.ceil(startOfDay(start).getTime() / 1000) : 0, [start])
  const periodEnd = useMemo(() => end ? Math.ceil(endOfDay(end).getTime() / 1000) : 0, [end])

  const { loading, data, error } = useQuery(OPERATION_TRAFFIC_DOWNLOAD_UPLOAD, {
    variables: {
      start: start ? periodStart.toString() : range,
      end: end ? periodEnd.toString() : null,
      sf_ids: focus ? [focus.sf_id] : selected.map(({ sf_id }) => sf_id)
    },
  })
  const chartRange = useMemo(() => {
    if (range) {
      return range
    }
    const diff = moment(end).diff(moment(start), 'days')
    if (diff <= 1) {
      return TimeRange.Today
    } else if (diff <= 7) {
      return TimeRange.LastSevenDays
    } else if (diff <= 30) {
      return TimeRange.LastThirtyDays
    } else {
      return TimeRange.LastTwelveMonths
    }

  }, [range, end, start])
  const graph = useMemo(() => {
    if (!loading && !error) {
      return data.monitoringGraphWan
    }
  }, [loading, data, error])

  return (
    <Stack
      sx={{
        flex: 1,
        fontSize: '12px',
        border: '1px solid #DAE0E7',
        borderRadius: '24px',
        padding: '16px 24px 8px 24px',
        height: '0',
        minHeight: '220px',
        maxHeight: '300px',
        gap: '25px',
      }}
    >
      <Title titleKey={t('stats.traffic.title')} bulletless>
        <DateFilter
          placement='left'
          onReset={() => setPeriod({ start: null, end: null })}
          startDate={start}
          endDate={end}
          onSubmit={(newStart, newEnd) => {
            setRange(null)
            setPeriod({ start: newStart, end: newEnd })
          }}
          placeholderKey={'stats.traffic.date_filter'}
          showPresets={true}
          showPresetLabel={true}
          defaultPreset="last7days"
          blockFutureDates={true}
          inputSx={{
            height: { xs: '24px', sm: '28px', lg: '32px' }
          }}
        />
      </Title>
      {loading ? (
        <ChartSkeleton />
      ) : !graph?.timestamps?.length ? (
        <ZeroStateMissingData
          message={t('stats.empty_data_from_server')}
          iconlabel={'square-poll-vertical'}
        />
      ) : (
        <Stack sx={{ display: 'flex', flex: 1, height: '100%' }}>
          <Chart data={graph} range={chartRange} />
        </Stack>
      )}
    </Stack>
  )
}

export default ActiveUsers
